import api from './axiosConfig';

export interface DashboardSummary {
    total: number;
    open: number;
    approved: number;
    rejected: number;
}

export interface TrendData {
    month: string;
    count: number;
}

export interface CategoryBreakdown {
    electricity: number;
    waste: number;
    water: number;
    waste_water: number;
}

export interface DashboardCounts {
    approved: number;
    rejected: number;
    open: number;
    all: number;
}

export interface RecentIndigent {
    id: string;
    indigent_number: string;
    name: string;
    electricity_meter_no: string;
    status: string;
}

export interface RecentUser {
    username: string;
    roles: string[];
    profile_picture: string | null;
}

export interface DashboardResponse {
    summary: DashboardSummary;
    category_breakdown: CategoryBreakdown;
    recent_indigents: RecentIndigent[];
    recent_users: RecentUser[];
    counts?: DashboardCounts;
}

export interface DashboardParams {
    period?: string;
    trend_type?: string;
    status_filter?: string;
}

export interface BarGraphParams {
    status_filter?: string;
    trend_type?: string;
}

export const dashboardService = {
    getDashboardStats: async (params: DashboardParams = {}): Promise<DashboardResponse> => {
        try {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, value.toString());
                }
            });
            
            const response = await api.get<any>(`/dashboard/stats?${queryParams.toString()}`);
            const data = response.data || {};

            const counts: DashboardCounts | undefined = data.counts ? {
                approved: Number(data.counts.approved ?? 0),
                rejected: Number(data.counts.rejected ?? 0),
                open: Number(data.counts.open ?? 0),
                all: Number(data.counts.all ?? 0),
            } : undefined;

            // Prefer counts if present; otherwise fallback to any provided summary
            const summary: DashboardSummary = counts
                ? {
                    total: counts.all,
                    open: counts.open,
                    approved: counts.approved,
                    rejected: counts.rejected,
                  }
                : (data.summary ?? { total: 0, open: 0, approved: 0, rejected: 0 });

            const category_breakdown: CategoryBreakdown = data.category_breakdown ?? {
                electricity: 0,
                waste: 0,
                water: 0,
                waste_water: 0,
            };

            const recent_indigents: RecentIndigent[] = data.recent_indigents ?? [];
            const recent_users: RecentUser[] = data.recent_users ?? [];

            return {
                summary,
                category_breakdown,
                recent_indigents,
                recent_users,
                counts,
            };
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
            throw error;
        }
    },

    getBarGraph: async (params: BarGraphParams = {}): Promise<{ trend: TrendData[], counts?: DashboardCounts }> => {
        try {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    queryParams.append(key, value.toString());
                }
            });

            const response = await api.get<{ trend: TrendData[], counts?: any }>(`/dashboard/bar-graph?${queryParams.toString()}`);
            const data = response.data;
            
            const counts: DashboardCounts | undefined = data.counts ? {
                approved: Number(data.counts.approved ?? 0),
                rejected: Number(data.counts.rejected ?? 0),
                open: Number(data.counts.open ?? 0),
                all: Number(data.counts.all ?? 0),
            } : undefined;
            
            return {
                trend: data.trend,
                counts
            };
        } catch (error) {
            console.error('Error fetching bar graph data:', error);
            throw error;
        }
    },

    formatStatus: (status: string): string => {
        switch (status) {
            case 'pending':
                return 'Pending';
            case 'approved':
                return 'Approved';
            case 'rejected':
                return 'Rejected';
            case 'reviewed':
                return 'Reviewed';
            default:
                return status.charAt(0).toUpperCase() + status.slice(1);
        }
    },

    getStatusColor: (status: string): string => {
        switch (status) {
            case 'pending':
                return '#F59E0B';
            case 'approved':
                return '#10B981';
            case 'rejected':
                return '#EF4444';
            case 'reviewed':
                return '#3B82F6';
            default:
                return '#6B7280';
        }
    },

    formatCategoryName: (category: string): string => {
        switch (category) {
            case 'electricity':
                return 'Electricity';
            case 'waste':
                return 'Waste';
            case 'water':
                return 'Water';
            case 'waste_water':
                return 'Waste Water';
            default:
                return category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ');
        }
    }
};
